import { useEffect, useRef, useState } from "react";
import {
  LiveKitRoom, RoomAudioRenderer, ControlBar, GridLayout, ParticipantTile,
  useTracks, useParticipants, useLocalParticipant, useChat,
} from "@livekit/components-react";
import { Track } from "livekit-client";
import "@livekit/components-styles";
import { X, Radio, Loader2, Video, Mic, Users, MessageCircle, Circle, Send, StopCircle, PhoneOff } from "lucide-react";
import { portalApi, apiError, uploadMedia } from "@/lib/portalApi";
import { toast } from "sonner";

const initials = (n) => (n || "?").split(" ").map((w) => w[0]).slice(0, 2).join("").toUpperCase();

function VideoTiles() {
  const tracks = useTracks(
    [{ source: Track.Source.Camera, withPlaceholder: false }, { source: Track.Source.ScreenShare, withPlaceholder: false }],
    { onlySubscribed: false },
  );
  if (tracks.length === 0) return null;
  return (
    <GridLayout tracks={tracks} style={{ height: "320px" }} className="rounded-lg overflow-hidden">
      <ParticipantTile />
    </GridLayout>
  );
}

function Participants() {
  const participants = useParticipants();
  return (
    <div data-testid="live-participants" className="mb-4">
      <p className="flex items-center gap-2 text-[10px] tracking-widest uppercase text-[#C9BEB0] mb-2">
        <Users className="w-3.5 h-3.5" strokeWidth={1.5} />On the call · {participants.length}
      </p>
      <div className="flex flex-wrap gap-2">
        {participants.map((p) => (
          <span key={p.identity} data-testid={`participant-${p.identity}`}
            className={`inline-flex items-center gap-2 rounded-full pl-1.5 pr-3 py-1 text-xs border ${p.isSpeaking ? "border-[#E8B486] bg-[#E8B486]/15" : "border-white/15 bg-white/5"}`}>
            <span className="w-6 h-6 rounded-full bg-[#A9724E] text-[#F4EFE6] flex items-center justify-center text-[9px] font-serif-display">{initials(p.name || p.identity)}</span>
            {p.name || "Vessel"}{p.isLocal && <span className="text-[#C9BEB0]">· you</span>}
          </span>
        ))}
      </div>
    </div>
  );
}

function ChatPanel() {
  const { chatMessages, send, isSending } = useChat();
  const [text, setText] = useState("");
  const endRef = useRef(null);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [chatMessages]);
  const submit = async (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    await send(text.trim());
    setText("");
  };
  return (
    <div data-testid="live-chat" className="flex flex-col h-full bg-white/5 rounded-lg border border-white/10">
      <p className="flex items-center gap-2 text-[10px] tracking-widest uppercase text-[#C9BEB0] px-3 py-2.5 border-b border-white/10">
        <MessageCircle className="w-3.5 h-3.5" strokeWidth={1.5} />Chat
      </p>
      <div className="flex-1 overflow-y-auto px-3 py-2 space-y-2 min-h-[140px] max-h-[220px]">
        {chatMessages.length === 0 && <p className="text-xs text-[#8a7a6a] font-body">Not into talking? Type here instead.</p>}
        {chatMessages.map((m, i) => (
          <div key={i} className="text-sm">
            <span className="text-[#E8B486] font-body text-xs">{m.from?.name || m.from?.identity || "Vessel"}</span>
            <p className="text-[#F4EFE6] font-body leading-snug whitespace-pre-wrap">{m.message}</p>
          </div>
        ))}
        <div ref={endRef} />
      </div>
      <form onSubmit={submit} className="flex items-center gap-2 p-2 border-t border-white/10">
        <input data-testid="live-chat-input" value={text} onChange={(e) => setText(e.target.value)} placeholder="Say something…"
          className="flex-1 bg-transparent border border-white/15 focus:border-[#E8B486] rounded-full px-3 py-1.5 text-sm text-[#F4EFE6] outline-none" />
        <button data-testid="live-chat-send" type="submit" disabled={isSending || !text.trim()} className="text-[#E8B486] disabled:opacity-40 p-1.5"><Send className="w-4 h-4" strokeWidth={1.5} /></button>
      </form>
    </div>
  );
}

function RoomStage({ video, isHost, sessionId, onClose }) {
  const { localParticipant } = useLocalParticipant();
  const [recording, setRecording] = useState(false);
  const [pendingBlob, setPendingBlob] = useState(null);
  const [saving, setSaving] = useState(false);
  const [meta, setMeta] = useState({ title: "", community: false, library: false });
  const [ending, setEnding] = useState(false);
  const recorderRef = useRef(null);
  const chunksRef = useRef([]);

  const startRecording = () => {
    try {
      const tracks = [];
      localParticipant?.trackPublications?.forEach((pub) => {
        if (pub.track?.mediaStreamTrack) tracks.push(pub.track.mediaStreamTrack);
      });
      if (tracks.length === 0) { toast.error("Turn on your mic or camera first, then record."); return; }
      const stream = new MediaStream(tracks);
      const hasVideo = tracks.some((t) => t.kind === "video");
      const mime = hasVideo
        ? (MediaRecorder.isTypeSupported("video/webm;codecs=vp8,opus") ? "video/webm;codecs=vp8,opus" : "video/webm")
        : "audio/webm";
      const rec = new MediaRecorder(stream, { mimeType: mime });
      chunksRef.current = [];
      rec.ondataavailable = (e) => { if (e.data.size > 0) chunksRef.current.push(e.data); };
      rec.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: mime.split(";")[0] });
        setPendingBlob(blob);
        setMeta({ title: "Recorded session", community: false, library: false });
      };
      rec.start(1000);
      recorderRef.current = rec;
      setRecording(true);
      toast.success("Recording started.");
    } catch (e) { toast.error("Couldn't start recording on this device."); }
  };

  const stopRecording = () => {
    try { recorderRef.current?.stop(); } catch {}
    setRecording(false);
  };

  const saveRecording = async () => {
    if (!pendingBlob) return;
    setSaving(true);
    try {
      const file = new File([pendingBlob], `session.webm`, { type: pendingBlob.type });
      const scope = meta.community || meta.library ? "cohort" : "private";
      const up = await uploadMedia(file, scope);
      await portalApi.post("/portal/recordings", {
        path: up.path, title: meta.title || "Recorded session",
        publish_community: meta.community, publish_library: meta.library, session_id: sessionId || null,
      });
      toast.success("Saved to your page.");
      setPendingBlob(null);
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };

  const endCall = async () => {
    setEnding(true);
    try { await portalApi.post(`/portal/live/session/${sessionId}/end`); toast.success("Call ended."); onClose(); }
    catch (e) { toast.error(apiError(e)); setEnding(false); }
  };

  return (
    <>
      <div className="grid md:grid-cols-[1fr_260px] gap-4">
        <div>
          <RoomAudioRenderer />
          {video && <div className="mb-3"><VideoTiles /></div>}
          <Participants />
          <div className="flex flex-wrap items-center gap-2">
            <ControlBar variation="minimal" controls={{ microphone: true, camera: video, screenShare: true, leave: true }} />
            {!recording ? (
              <button data-testid="live-record-start" onClick={startRecording}
                className="inline-flex items-center gap-2 rounded-full bg-[#8a3b3b] hover:bg-[#7a3333] px-4 py-2 text-sm transition-colors">
                <Circle className="w-4 h-4 fill-current" strokeWidth={0} />Record
              </button>
            ) : (
              <button data-testid="live-record-stop" onClick={stopRecording}
                className="inline-flex items-center gap-2 rounded-full bg-[#b45c5c] hover:bg-[#a04f4f] px-4 py-2 text-sm transition-colors animate-pulse">
                <StopCircle className="w-4 h-4" strokeWidth={1.5} />Stop & save
              </button>
            )}
            {isHost && sessionId && (
              <button data-testid="live-end-call" onClick={endCall} disabled={ending}
                className="inline-flex items-center gap-2 rounded-full border border-white/20 hover:bg-white/10 px-4 py-2 text-sm transition-colors disabled:opacity-50">
                <PhoneOff className="w-4 h-4" strokeWidth={1.5} />End call
              </button>
            )}
          </div>
        </div>
        <div className="min-h-[220px]"><ChatPanel /></div>
      </div>

      {pendingBlob && (
        <div className="fixed inset-0 z-[70] bg-black/60 flex items-center justify-center p-4" data-testid="save-recording-modal">
          <div className="bg-[#F4EFE6] text-[#2A2825] rounded-2xl w-full max-w-md p-6 shadow-2xl">
            <p className="tracking-overline text-[10px] uppercase text-[#8a7a6a] mb-1">Your recording is ready</p>
            <h3 className="font-serif-display text-2xl mb-4">Save this session</h3>
            {pendingBlob.type.startsWith("audio")
              ? <audio src={URL.createObjectURL(pendingBlob)} controls className="w-full mb-4" />
              : <video src={URL.createObjectURL(pendingBlob)} controls className="w-full rounded-lg bg-black mb-4 max-h-52" />}
            <label className="block text-sm text-[#5E4B3C] mb-1.5">Title</label>
            <input data-testid="recording-title" value={meta.title} onChange={(e) => setMeta({ ...meta, title: e.target.value })}
              className="w-full bg-white border border-[#D8CEBE] rounded-md px-3 py-2 font-body outline-none focus:border-[#A9724E] mb-4" />
            <label className="flex items-center gap-2 text-sm text-[#2A2825] mb-2 cursor-pointer">
              <input data-testid="recording-publish-community" type="checkbox" checked={meta.community} onChange={(e) => setMeta({ ...meta, community: e.target.checked })} />
              Share in the Community well
            </label>
            <label className="flex items-center gap-2 text-sm text-[#2A2825] mb-5 cursor-pointer">
              <input data-testid="recording-publish-library" type="checkbox" checked={meta.library} onChange={(e) => setMeta({ ...meta, library: e.target.checked })} />
              Add to the Library
            </label>
            <p className="text-xs text-[#8a7a6a] mb-4">Left unchecked, it stays private on your own page.</p>
            <div className="flex items-center justify-end gap-3">
              <button onClick={() => setPendingBlob(null)} className="text-sm text-[#8a7a6a]">Discard</button>
              <button data-testid="recording-save" onClick={saveRecording} disabled={saving}
                className="rounded-full bg-[#A9724E] hover:bg-[#925C3B] text-[#F4EFE6] px-6 py-2.5 text-sm transition-colors disabled:opacity-50">
                {saving ? "Saving…" : "Save recording"}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

// scope: "cohort" | "witness" | "member" | "gathering" | "session"
export default function LiveRoom({ scope, targetId, title, isHost = false, sessionId, onClose }) {
  const [session, setSession] = useState(null);
  const [error, setError] = useState("");
  const [video, setVideo] = useState(false);
  const [joining, setJoining] = useState(false);

  const join = async (withVideo) => {
    setJoining(true); setError(""); setVideo(withVideo);
    try {
      const { data } = await portalApi.post("/portal/live/token", { scope, target_id: targetId, video: withVideo });
      setSession(data);
    } catch (e) { setError(apiError(e)); } finally { setJoining(false); }
  };

  useEffect(() => () => setSession(null), []);

  return (
    <div className="fixed inset-0 z-[60] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" data-testid="live-room-modal">
      <div className="bg-[#1F1D1B] text-[#F4EFE6] rounded-2xl w-full max-w-3xl overflow-hidden border border-white/10 max-h-[92vh] overflow-y-auto">
        <div className="flex items-center justify-between px-6 py-4 border-b border-white/10 sticky top-0 bg-[#1F1D1B] z-10">
          <div className="flex items-center gap-2.5">
            <Radio className="w-5 h-5 text-[#E8B486]" strokeWidth={1.5} />
            <span className="font-serif-display text-lg">{title || "Live room"}</span>
          </div>
          <button onClick={onClose} data-testid="live-room-close" className="text-[#C9BEB0] hover:text-white p-1"><X className="w-5 h-5" strokeWidth={1.5} /></button>
        </div>

        <div className="p-6">
          {!session ? (
            <div className="text-center py-8">
              <p className="font-serif-display text-2xl mb-2">Gather live</p>
              <p className="text-[#C9BEB0] font-body text-sm mb-8 max-w-sm mx-auto">Join with audio or video like a call. Others can see who's here, join in, or type in the chat.</p>
              {error && <p className="text-[#e0a19a] text-sm mb-4" data-testid="live-room-error">{error}</p>}
              <div className="flex items-center justify-center gap-3">
                <button onClick={() => join(false)} disabled={joining} data-testid="live-join-audio"
                  className="inline-flex items-center gap-2 rounded-full bg-[#A9724E] hover:bg-[#925C3B] px-6 py-3 text-sm transition-colors disabled:opacity-50">
                  {joining ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mic className="w-4 h-4" strokeWidth={1.5} />}Join with audio
                </button>
                <button onClick={() => join(true)} disabled={joining} data-testid="live-join-video"
                  className="inline-flex items-center gap-2 rounded-full border border-white/20 hover:bg-white/10 px-6 py-3 text-sm transition-colors disabled:opacity-50">
                  <Video className="w-4 h-4" strokeWidth={1.5} />Join with video
                </button>
              </div>
            </div>
          ) : (
            <LiveKitRoom serverUrl={session.server_url} token={session.token} connect audio video={video}
              options={{ adaptiveStream: true, dynacast: true }} onError={(e) => setError(e.message)}
              onDisconnected={onClose} data-lk-theme="default">
              <RoomStage video={video} isHost={isHost} sessionId={sessionId} onClose={onClose} />
            </LiveKitRoom>
          )}
          {error && session && <p className="text-[#e0a19a] text-sm mt-3">{error}</p>}
        </div>
      </div>
    </div>
  );
}
